// Makes AI's changes to a project's models: the changes for EPM decisions chosen on the scorecard (a future state), and
// changes described by the user (see common/model-changes). This works out the node and edge changes for each model
// from the project's graphs; the callers save them.

import { v4 } from "uuid";
import { createLinqsetChanges, LinqsetChanges } from "../common/linqset-change-model";
import { Duration, EDGE_TYPE, FREQUENCY_TYPE, GraphData, LinqsetEdge, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE, ORGANISATION_MODEL_KEY } from "../common/linqset-base";
import { createLinqsetNodeWithoutLinqset } from "../common/linqset-model";
import { ModelChangeKind } from "../common/model-changes";
import { edgesForWorkspace, nodesForWorkspace } from "../common/analysis-model";
import { Organisation } from "../common/organisation";

// a change to make: an AIEpmChange (which has the decision it carries out) or an AIModelChange
export interface PlannedChange {
    kind: ModelChangeKind;
    decision?: string;
    action: string | null;
    model: string | null;
    role: string | null;
    newRole: string | null;
    system: string | null;
    newSystem?: string | null;
    percent: number | null;
    people: number | null;
    cost?: number | null;
    from?: string | null;           // share: the step or information the items leave. add: what the new step follows
    items?: number | null;          // volume: items a year
    output?: string | null;         // add: the information the new step creates
    minutes?: number | null;        // add: the new step's time per item
    description: string;
}

// a new system whose name says it's an AI agent (AI is told to name them so) is made as one
export const isAIAgentName = (name: string) => /\b(ai|agents?|agentic|copilots?|co-pilots?|assistants?|bots?|chatbots?|gpt|llm)\b/i.test(name);

// what was done with each change, to show the user
export interface EpmAppliedChange {
    decision?: string;
    description: string;
    applied: boolean;
    reason?: string;            // why it couldn't be made
}

export interface EpmFutureStatePlan {
    changes: { [modelKey: string]: LinqsetChanges };      // the organisation model's (roles and systems) are saved first
    applied: EpmAppliedChange[];
}

export interface EpmFutureStateContext {
    organisationKey: string;
    projectKey: string;
    models: { _key: string; name: string }[];
    organisationModel: GraphData;                         // the project's roles and systems
    graphs: { [modelKey: string]: GraphData };            // the project's graphs of the models the changes are in
    newKey?: () => string;
}

const sameName = (a: string | null | undefined, b: string | null | undefined) =>
    !!a && !!b && a.trim().toLowerCase() === b.trim().toLowerCase();

const toSeconds = (d: Duration | undefined) => d ? ((d.days || 0) * 24 * 3600) + ((d.hours || 0) * 3600) + ((d.minutes || 0) * 60) + (d.seconds || 0) : 0;

const fromSeconds = (total: number): Duration => {
    const seconds = Math.round(total);
    return { days: Math.floor(seconds / 86400), hours: Math.floor((seconds % 86400) / 3600), minutes: Math.floor((seconds % 3600) / 60), seconds: seconds % 60 };
};

// changes to roles and systems themselves, rather than to an action
const ORGANISATION_KINDS: ModelChangeKind[] = ['staff', 'replaceSystem', 'replaceRole', 'roleCost', 'systemCost'];

// how items go on from information within a model, and to other models
const ONWARD_EDGES = [EDGE_TYPE.SUPPORT, EDGE_TYPE.REALISE];
const OTHER_MODEL_EDGES = [EDGE_TYPE.JOINS, EDGE_TYPE.USED_IN];

// a model's graph as the workspace has it. A project's graph comes with current state's edges as well as its own, and
// once the project has changed the model only its own count
export const graphForWorkspace = (graph: GraphData, organisation: Pick<Organisation, 'models'>, project?: { _key: string; sandbox?: boolean }): GraphData =>
    ({ nodes: nodesForWorkspace(graph.nodes, graph.edges, organisation, project), edges: edgesForWorkspace(graph.edges, organisation, project) });

// the models a set of changes to actions touches, by key, so their graphs can be loaded. A replacement touches every
// model its role or system is used in, which the caller knows
export const modelsForChanges = (changes: PlannedChange[], models: { _key: string; name: string }[]): string[] =>
    [...new Set(changes.filter((c) => !ORGANISATION_KINDS.includes(c.kind)).map((c) => models.find((m) => sameName(m.name, c.model))?._key).filter((k): k is string => !!k))];

export const planEpmFutureState = (changes: PlannedChange[], context: EpmFutureStateContext): EpmFutureStatePlan => {
    const newKey = context.newKey || v4;
    const plan: EpmFutureStatePlan = { changes: {}, applied: [] };
    const changesFor = (modelKey: string) => plan.changes[modelKey] ||= createLinqsetChanges();
    const organisationChanges = changesFor(ORGANISATION_MODEL_KEY);

    // roles and systems by name, including those this creates
    const organisationNodes = [...context.organisationModel.nodes];
    const findOrganisationNode = (superType: NODE_SUPERTYPE, name: string | null | undefined) =>
        organisationNodes.find((n) => n.superType === superType && sameName(n.name, name));
    const getOrCreate = (superType: NODE_SUPERTYPE, name: string, people?: number | null, like?: LinqsetNode) => {
        const existing = findOrganisationNode(superType, name);
        if (existing) return existing;
        const type = superType === NODE_SUPERTYPE.ROLE ? NODE_TYPE.ROLE : isAIAgentName(name) ? NODE_TYPE.AI_AGENT : NODE_TYPE.SYSTEM;
        const node = createLinqsetNodeWithoutLinqset(newKey(), superType, type,
            context.organisationKey, ORGANISATION_MODEL_KEY, context.projectKey, name.trim());
        // a replacement starts with what it replaces (its people and costs), so the figures stay comparable
        if (like?.properties) node.properties = { ...structuredClone(like.properties), projectKey: context.projectKey, tagKeys: {} };
        if (superType === NODE_SUPERTYPE.ROLE && people) node.properties!.people = people;
        organisationNodes.push(node);
        organisationChanges.newNodes.push(node);
        return node;
    };

    // a property change, merged with any other change to the same node
    const changeProperties = (changesForModel: LinqsetChanges, node: LinqsetNode, properties: any) => {
        if (changesForModel.newNodes.includes(node)) { Object.assign(node.properties!, properties); return; }
        const existing = changesForModel.nodeChanges.find((c) => c._key === node._key);
        if (existing) existing.properties = { ...existing.properties, ...properties };
        else changesForModel.nodeChanges.push({ _key: node._key, properties });
    };

    // the nodes and edges each graph has left, as changes delete some and add others
    const nodes: { [modelKey: string]: LinqsetNode[] } = Object.fromEntries(Object.entries(context.graphs).map(([k, g]) => [k, [...g.nodes]]));
    const edges: { [modelKey: string]: LinqsetEdge[] } = Object.fromEntries(Object.entries(context.graphs).map(([k, g]) => [k, [...g.edges]]));
    edges[ORGANISATION_MODEL_KEY] = [...(context.organisationModel.edges || [])];
    const deleteEdge = (modelKey: string, edge: LinqsetEdge) => {
        edges[modelKey] = edges[modelKey].filter((e) => e._key !== edge._key);
        const changesForModel = changesFor(modelKey);
        const added = changesForModel.newEdges.findIndex((e) => e._key === edge._key);
        if (added >= 0) changesForModel.newEdges.splice(added, 1);
        else if (!changesForModel.deletedEdges.some((e) => e._key === edge._key)) changesForModel.deletedEdges.push(edge);
    };
    const hasEdge = (modelKey: string, type: EDGE_TYPE, sourceKey: string, targetKey: string) =>
        edges[modelKey].some((e) => e.type === type && e.sourceKey === sourceKey && e.targetKey === targetKey);
    // an edge from a role or system to the action, drawn from where that role or system already appears in the model
    const addEdge = (modelKey: string, type: EDGE_TYPE, from: LinqsetNode, action: LinqsetNode) => {
        if (hasEdge(modelKey, type, from._key, action._key)) return;
        const visual = edges[modelKey].find((e) => e.sourceKey === from._key && (e.type === EDGE_TYPE.PERFORM || e.type === EDGE_TYPE.AUTOMATE))?.sourceVisualisationKey;
        const edge = { _key: newKey(), type, modelKey, sourceKey: from._key, targetKey: action._key, sourceVisualisationKey: visual || newKey(),
            targetVisualisationKey: action._key, outputPercentage: 100 } as LinqsetEdge;
        edges[modelKey].push(edge);
        changesFor(modelKey).newEdges.push(edge);
    };
    // takes the action from roles, with the systems shown being used by them there
    const removeRoles = (modelKey: string, action: LinqsetNode, role?: LinqsetNode) => {
        const performs = edges[modelKey].filter((e) => e.type === EDGE_TYPE.PERFORM && e.targetKey === action._key && (!role || e.sourceKey === role._key));
        for (const perform of performs) {
            deleteEdge(modelKey, perform);
            // a role appears once for each action it performs; once its last is gone, so are the systems shown with it
            if (!edges[modelKey].some((e) => e.type === EDGE_TYPE.PERFORM && e.sourceVisualisationKey === perform.sourceVisualisationKey)) {
                edges[modelKey].filter((e) => e.type === EDGE_TYPE.USEBY && e.targetVisualisationKey === perform.sourceVisualisationKey).forEach((e) => deleteEdge(modelKey, e));
            }
        }
        return performs.length;
    };
    // moves every edge of a role or system, in every graph loaded, to its replacement. Each stays where it's drawn
    const replaceNode = (from: LinqsetNode, to: LinqsetNode) => {
        let moved = 0;
        for (const modelKey of Object.keys(edges)) {
            for (const e of edges[modelKey].filter((e) => e.sourceKey === from._key || e.targetKey === from._key)) {
                deleteEdge(modelKey, e);
                const sourceKey = e.sourceKey === from._key ? to._key : e.sourceKey;
                const targetKey = e.targetKey === from._key ? to._key : e.targetKey;
                if (!hasEdge(modelKey, e.type, sourceKey, targetKey)) {
                    const edge = { ...e, _key: newKey(), sourceKey, targetKey, originalKey: undefined } as LinqsetEdge;
                    edges[modelKey].push(edge);
                    changesFor(modelKey).newEdges.push(edge);
                }
                moved++;
            }
        }
        return moved;
    };
    // gives a role the number of people asked for. A role filled by people (or a team) has their capacity, not its own
    // count of people, so it's their number that changes: the team grows, or people like those it has join it, each
    // with the same hours, cost and share of their time on the role. Returns why it couldn't, if it couldn't
    const staff = (role: LinqsetNode, people: number): string | undefined => {
        const fills = edges[ORGANISATION_MODEL_KEY].filter((e) => e.type === EDGE_TYPE.FULFILS && e.targetKey === role._key)
            .map((edge) => ({ edge, person: organisationNodes.find((n) => n._key === edge.sourceKey && n.superType === NODE_SUPERTYPE.PEOPLE) }))
            .filter((f): f is { edge: LinqsetEdge; person: LinqsetNode } => !!f.person);
        if (!fills.length) {
            changeProperties(organisationChanges, role, { people });
            return undefined;
        }
        const count = (p: LinqsetNode) => (p.type === NODE_TYPE.TEAM ? p.properties?.people || 1 : 1);
        const current = fills.reduce((sum, f) => sum + count(f.person), 0);
        if (people === current) return `${role.name} already has ${people}`;
        if (people < current) return `${role.name}'s people are in the model, so fewer is made by taking them off it there`;

        const team = fills.find((f) => f.person.type === NODE_TYPE.TEAM);
        if (team) {
            changeProperties(organisationChanges, team.person, { people: count(team.person) + people - current });
            return undefined;
        }
        const like = fills[0];
        for (let i = 1; i <= people - current; i++) {
            const person = createLinqsetNodeWithoutLinqset(newKey(), NODE_SUPERTYPE.PEOPLE, like.person.type, context.organisationKey, ORGANISATION_MODEL_KEY,
                context.projectKey, `${like.person.name} (new ${i})`);
            if (like.person.properties) person.properties = { ...structuredClone(like.person.properties), projectKey: context.projectKey, tagKeys: {} };
            organisationNodes.push(person);
            organisationChanges.newNodes.push(person);
            const edge = { ...like.edge, _key: newKey(), sourceKey: person._key, sourceVisualisationKey: newKey(), originalKey: undefined } as LinqsetEdge;
            edges[ORGANISATION_MODEL_KEY].push(edge);
            organisationChanges.newEdges.push(edge);
        }
        return undefined;
    };
    const retime = (modelKey: string, action: LinqsetNode, percent: number) =>
        changeProperties(changesFor(modelKey), action, { duration: fromSeconds(toSeconds(action.properties?.duration) * percent / 100) });
    // a connection's new share of items, kept with the other changes to the edge
    const setShare = (modelKey: string, edge: LinqsetEdge, percent: number) => {
        const changed = { ...edge, outputPercentage: Math.round(percent * 100) / 100 } as LinqsetEdge;
        edges[modelKey] = edges[modelKey].map((e) => (e._key === edge._key ? changed : e));
        const changesForModel = changesFor(modelKey);
        const added = changesForModel.newEdges.findIndex((e) => e._key === edge._key);
        if (added >= 0) { changesForModel.newEdges[added] = changed; return; }
        const existing = changesForModel.edgeChanges.findIndex((e) => e._key === edge._key);
        if (existing >= 0) changesForModel.edgeChanges[existing] = changed;
        else changesForModel.edgeChanges.push(changed);
    };
    // the share of items going from a point in the flow (information, or a step through the information it creates)
    // to a step: that connection gets the share, and the other connections leaving the same information share the
    // rest in proportion to what they carry today. Returns why it couldn't, if it couldn't
    const share = (modelKey: string, from: LinqsetNode, to: LinqsetNode, percent: number): string | undefined => {
        const flows = (sourceKey: string) => edges[modelKey].filter((e) => e.sourceKey === sourceKey && (e.type === EDGE_TYPE.SUPPORT || e.type === EDGE_TYPE.CREATE || e.type === EDGE_TYPE.JOINS));
        let source: LinqsetNode | undefined = from;
        if (from.superType === NODE_SUPERTYPE.ACTION) {
            const created = new Set(flows(from._key).map((e) => e.targetKey));
            source = (nodes[modelKey] || []).find((n) => created.has(n._key) && flows(n._key).some((e) => e.targetKey === to._key));
        }
        if (!source) return `${from.name} doesn't send items to ${to.name}`;
        const out = flows(source._key);
        const chosen = out.filter((e) => e.targetKey === to._key);
        if (!chosen.length) return `${from.name} doesn't send items to ${to.name}`;
        const carried = (e: LinqsetEdge) => (e.outputPercentage ?? 100);
        const others = out.filter((e) => e.targetKey !== to._key);
        const total = out.reduce((sum, e) => sum + carried(e), 0);
        const rest = Math.max(0, total - percent);
        const othersTotal = others.reduce((sum, e) => sum + carried(e), 0);
        chosen.forEach((e) => setShare(modelKey, e, percent / chosen.length));
        others.forEach((e) => setShare(modelKey, e, othersTotal > 0 ? carried(e) / othersTotal * rest : rest / others.length));
        return undefined;
    };

    // an edge in the flow, unless the same one is already there
    const addFlow = (modelKey: string, edge: Partial<LinqsetEdge> & Pick<LinqsetEdge, 'type' | 'sourceKey' | 'targetKey'>) => {
        if (hasEdge(modelKey, edge.type, edge.sourceKey, edge.targetKey)) return;
        const added = { ...edge, _key: newKey(), modelKey, originalKey: undefined } as LinqsetEdge;
        edges[modelKey].push(added);
        changesFor(modelKey).newEdges.push(added);
    };
    // where a node is drawn, from the edges it already has
    const visualOf = (modelKey: string, node: LinqsetNode) =>
        edges[modelKey].find((e) => e.sourceKey === node._key)?.sourceVisualisationKey || edges[modelKey].find((e) => e.targetKey === node._key)?.targetVisualisationKey || node._key;
    // takes a node out of a model, with every edge it has there
    const deleteNode = (modelKey: string, node: LinqsetNode) => {
        edges[modelKey].filter((e) => e.sourceKey === node._key || e.targetKey === node._key).forEach((e) => deleteEdge(modelKey, e));
        nodes[modelKey] = nodes[modelKey].filter((n) => n._key !== node._key);
        const changesForModel = changesFor(modelKey);
        const added = changesForModel.newNodes.indexOf(node);
        if (added >= 0) changesForModel.newNodes.splice(added, 1);
        else changesForModel.deletedNodes.push(node);
        changesForModel.nodeChanges = changesForModel.nodeChanges.filter((c) => c._key !== node._key);
    };

    // takes an action out of the flow: the information it works on goes straight to where its own information went, in
    // the same shares, and its own information goes too unless another step also creates it. Returns why it couldn't
    const remove = (modelKey: string, action: LinqsetNode): string | undefined => {
        const inputs = edges[modelKey].filter((e) => e.type === EDGE_TYPE.SUPPORT && e.targetKey === action._key);
        if (action.type === NODE_TYPE.CAPTURE || !inputs.length) return `${action.name} starts the flow, so it can't be taken out (it can take no time instead)`;
        const created = edges[modelKey].filter((e) => e.type === EDGE_TYPE.CREATE && e.sourceKey === action._key)
            .map((e) => nodes[modelKey].find((n) => n._key === e.targetKey))
            .filter((info): info is LinqsetNode => !!info && !edges[modelKey].some((e) => e.type === EDGE_TYPE.CREATE && e.targetKey === info._key && e.sourceKey !== action._key));
        const elsewhere = created.find((info) => edges[modelKey].some((e) => e.sourceKey === info._key && OTHER_MODEL_EDGES.includes(e.type)));
        if (elsewhere) return `${elsewhere.name} goes on to other models, so ${action.name} can't be taken out (it can take no time instead)`;

        for (const info of created) {
            for (const onward of edges[modelKey].filter((e) => e.sourceKey === info._key && ONWARD_EDGES.includes(e.type) && e.targetKey !== action._key)) {
                for (const input of inputs) {
                    addFlow(modelKey, { ...onward, sourceKey: input.sourceKey, sourceVisualisationKey: input.sourceVisualisationKey,
                        outputPercentage: Math.round((input.outputPercentage ?? 100) * (onward.outputPercentage ?? 100)) / 100 });
                }
            }
            deleteNode(modelKey, info);
        }
        removeRoles(modelKey, action);
        deleteNode(modelKey, action);
        return undefined;
    };

    // puts a new action in the flow after information, or after a step through the information most of its items go
    // through: the new step takes it on and creates its own information, which goes on where the items went from there
    // (what goes to other models still leaves where it did). Returns the new step, or why it couldn't
    const add = (modelKey: string, modelName: string, change: PlannedChange): LinqsetNode | string => {
        const name = change.action!.trim();
        if (nodes[modelKey].some((n) => n.superType === NODE_SUPERTYPE.ACTION && sameName(n.name, name))) return `${modelName} already has a step called ${name}`;
        const after = nodes[modelKey].find((n) => (n.superType === NODE_SUPERTYPE.ACTION || n.superType === NODE_SUPERTYPE.INFORMATION) && sameName(n.name, change.from));
        if (!after) return `${change.from} isn't a step or information in ${modelName}`;
        let info: LinqsetNode | undefined = after;
        if (after.superType === NODE_SUPERTYPE.ACTION) {
            // where the step's items split, the branch the new step's words name (a support step follows the support
            // request), else the one most items take
            const words = new Set(`${name} ${change.output || ''} ${change.description || ''}`.toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((w) => w.length > 2));
            const named = (n: LinqsetNode) => (n.name || '').toLowerCase().split(/[^\p{L}\p{N}]+/u).filter((w) => words.has(w)).length;
            const created = edges[modelKey].filter((e) => e.type === EDGE_TYPE.CREATE && e.sourceKey === after._key)
                .map((e) => ({ e, n: nodes[modelKey].find((n) => n._key === e.targetKey) }))
                .filter((c): c is { e: LinqsetEdge; n: LinqsetNode } => !!c.n)
                .sort((a, b) => named(b.n) - named(a.n) || (b.e.outputPercentage ?? 100) - (a.e.outputPercentage ?? 100));
            info = created[0]?.n;
            if (!info) return `${after.name} creates no information for a step to follow`;
        }

        const step = createLinqsetNodeWithoutLinqset(newKey(), NODE_SUPERTYPE.ACTION, NODE_TYPE.ACTION, context.organisationKey, modelKey, context.projectKey, name);
        if (change.minutes !== null && change.minutes !== undefined) step.properties!.duration = fromSeconds(change.minutes * 60);
        const output = createLinqsetNodeWithoutLinqset(newKey(), NODE_SUPERTYPE.INFORMATION, info.type, context.organisationKey, modelKey, context.projectKey,
            change.output?.trim() || `${name} done`);
        nodes[modelKey].push(step, output);
        changesFor(modelKey).newNodes.push(step, output);

        const infoVisual = visualOf(modelKey, info);
        for (const onward of edges[modelKey].filter((e) => e.sourceKey === info!._key && ONWARD_EDGES.includes(e.type))) {
            deleteEdge(modelKey, onward);
            addFlow(modelKey, { ...onward, sourceKey: output._key, sourceVisualisationKey: output._key });
        }
        addFlow(modelKey, { type: EDGE_TYPE.SUPPORT, sourceKey: info._key, targetKey: step._key, sourceVisualisationKey: infoVisual, targetVisualisationKey: step._key, outputPercentage: 100 });
        addFlow(modelKey, { type: EDGE_TYPE.CREATE, sourceKey: step._key, targetKey: output._key, sourceVisualisationKey: step._key, targetVisualisationKey: output._key, outputPercentage: 100 });
        return step;
    };

    for (const change of changes) {
        const result: EpmAppliedChange = { decision: change.decision, description: change.description, applied: false };
        plan.applied.push(result);

        // changes to roles and systems
        if (ORGANISATION_KINDS.includes(change.kind)) {
            const isRole = change.kind === 'staff' || change.kind === 'replaceRole' || change.kind === 'roleCost';
            const name = isRole ? change.role : change.system;
            const node = findOrganisationNode(isRole ? NODE_SUPERTYPE.ROLE : NODE_SUPERTYPE.SYSTEM, name);
            if (!node) { result.reason = `${name} isn't a ${isRole ? 'role' : 'system'} in the organisation`; continue; }

            if (change.kind === 'staff') {
                const reason = staff(node, change.people || 0);
                if (reason) { result.reason = reason; continue; }
            }
            else if (change.kind === 'roleCost') changeProperties(organisationChanges, node, { annualCost: change.cost || 0 });
            else if (change.kind === 'systemCost') changeProperties(organisationChanges, node, { fixedCost: change.cost || 0 });
            else {
                const replacement = getOrCreate(node.superType, (isRole ? change.newRole : change.newSystem)!, isRole ? change.people : undefined, node);
                if (replacement._key === node._key) { result.reason = `${node.name} can't replace itself`; continue; }
                if (!replaceNode(node, replacement)) { result.reason = `${node.name} isn't used in the project's models`; continue; }
            }
            result.applied = true;
            continue;
        }

        const model = context.models.find((m) => sameName(m.name, change.model));
        const modelNodes = model && nodes[model._key];

        // a new step, done by a role or a system, either of which may be new
        if (change.kind === 'add') {
            if (!model || !modelNodes || !change.action?.trim()) { result.reason = `${change.model} isn't a model in the project`; continue; }
            const step = add(model._key, model.name, change);
            if (typeof step === 'string') { result.reason = step; continue; }
            if (change.newRole?.trim()) addEdge(model._key, EDGE_TYPE.PERFORM, getOrCreate(NODE_SUPERTYPE.ROLE, change.newRole, change.people), step);
            if (change.system?.trim()) addEdge(model._key, EDGE_TYPE.AUTOMATE, getOrCreate(NODE_SUPERTYPE.SYSTEM, change.system), step);
            result.applied = true;
            continue;
        }

        const action = modelNodes?.find((n) => n.superType === NODE_SUPERTYPE.ACTION && sameName(n.name, change.action));
        if (!model || !action) { result.reason = `${change.action} isn't an action in ${change.model || 'the models'}`; continue; }

        switch (change.kind) {
            case 'remove': {
                const reason = remove(model._key, action);
                if (reason) { result.reason = reason; continue; }
                break;
            }
            case 'eliminate':
                changeProperties(changesFor(model._key), action, { duration: fromSeconds(0) });
                break;
            case 'retime':
                retime(model._key, action, change.percent ?? 100);
                break;
            case 'volume':
                changeProperties(changesFor(model._key), action, { frequency: { type: FREQUENCY_TYPE.YEAR, ocurrences: Math.round(change.items ?? 0) } });
                break;
            case 'share': {
                const from = modelNodes!.find((n) => (n.superType === NODE_SUPERTYPE.ACTION || n.superType === NODE_SUPERTYPE.INFORMATION) && sameName(n.name, change.from));
                if (!from) { result.reason = `${change.from} isn't a step or information in ${model.name}`; continue; }
                const reason = share(model._key, from, action, change.percent ?? 0);
                if (reason) { result.reason = reason; continue; }
                break;
            }
            case 'automate':
            case 'assist': {
                const system = getOrCreate(NODE_SUPERTYPE.SYSTEM, change.system!);
                if (change.kind === 'automate') removeRoles(model._key, action);
                addEdge(model._key, EDGE_TYPE.AUTOMATE, system, action);
                if (change.percent !== null && change.percent !== undefined) retime(model._key, action, change.percent);
                break;
            }
            case 'reassign': {
                const from = change.role ? findOrganisationNode(NODE_SUPERTYPE.ROLE, change.role) : undefined;
                if (change.role && !from) { result.reason = `${change.role} isn't a role in the organisation`; continue; }
                const to = getOrCreate(NODE_SUPERTYPE.ROLE, change.newRole!, change.people);
                if (from?._key === to._key) { result.reason = `${to.name} already performs it`; continue; }
                if (!removeRoles(model._key, action, from) && from) { result.reason = `${from.name} doesn't perform ${action.name}`; continue; }
                addEdge(model._key, EDGE_TYPE.PERFORM, to, action);
                break;
            }
        }
        result.applied = true;
    }

    // leave out models with nothing to save
    for (const [key, c] of Object.entries(plan.changes)) {
        if (!c.newNodes.length && !c.nodeChanges.length && !c.newEdges.length && !c.deletedEdges.length && !c.edgeChanges.length && !c.deletedNodes.length) delete plan.changes[key];
    }
    return plan;
};
